import React from 'react';
import { useNavigate } from 'react-router';
import { 
    Box, 
    Typography, 
    Button, 
    Container, 
    Paper,
    useTheme,
    alpha,
    Stack,
    Chip,
    Card
} from "@mui/material";
import { 
    RiShieldCrossLine, 
    RiHome5Line, 
    RiArrowLeftLine, 
    RiContactsBookLine,
    RiLockLine,
    RiUserForbidLine,
    RiSecurePaymentLine
} from "@remixicon/react";
import { Routes } from '../../types/enums/Routes.enum';

export interface AccessDeniedProps {
    title?: string;
    message?: string;
    showBackButton?: boolean;
    showHomeButton?: boolean;
    showContactInfo?: boolean;
    requiredPermissions?: Array<{
        modulo: string;
        permiso?: string;
    }>;
    customActions?: React.ReactNode;
    variant?: 'default' | 'minimal' | 'detailed';
}

const AccessDenied: React.FC<AccessDeniedProps> = ({
    title = "Acceso Denegado",
    message = "No tienes los permisos necesarios para acceder a esta sección.",
    showBackButton = true,
    showHomeButton = true,
    showContactInfo = false,
    requiredPermissions = [],
    customActions,
    variant = 'default'
}) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const handleGoHome = () => {
        navigate(Routes.DEFAULT);
    };

    const handleGoBack = () => {
        window.history.back();
    };

    const handleContactAdmin = () => {
        //TODO: Implementar lógica para contactar al administrador, por ejemplo: abrir modal, redirigir a soporte, etc.
        const mailtoLink = 'mailto:soporte@pj.gob.do?subject=Solicitud de Acceso - Portal Digital&body=Estimado equipo de soporte,%0D%0A%0D%0ASolicito acceso a la sección del portal que requiere los siguientes permisos:%0D%0A' + 
            requiredPermissions.map(p => `- ${p.modulo}${p.permiso ? '.' + p.permiso : ''}`).join('%0D%0A') + 
            '%0D%0A%0D%0AGracias por su atención.';
        
        window.location.href = mailtoLink;
    };

    const renderIcon = () => {
        const IconComponent = variant === 'detailed' ? RiUserForbidLine : RiShieldCrossLine;
        const iconSize = variant === 'minimal' ? 48 : { xs: 56, md: 72 };
        
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 3,
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 110, md: 130 },
                        height: { xs: 110, md: 130 },
                        borderRadius: '50%',
                        background: `
                            radial-gradient(circle at center, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.02)} 70%),
                            linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.04)} 0%, ${alpha(theme.palette.error.main, 0.06)} 100%)
                        `,
                        border: `3px solid ${alpha(theme.palette.error.main, 0.12)}`,
                        boxShadow: `
                            0 0 0 1px ${alpha(theme.palette.error.main, 0.04)},
                            0 8px 32px ${alpha(theme.palette.error.main, 0.08)}
                        `,
                        animation: 'gentleFloat 4s ease-in-out infinite',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            width: '140%',
                            height: '140%',
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(theme.palette.error.main, 0.04)} 0%, transparent 70%)`,
                            animation: 'rippleEffect 3s ease-in-out infinite',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            width: '160%',
                            height: '160%',
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(theme.palette.error.main, 0.02)} 0%, transparent 60%)`,
                            animation: 'rippleEffect 3s ease-in-out infinite 0.5s',
                            zIndex: 0,
                        }
                    }}
                >
                    <IconComponent
                        size={typeof iconSize === 'object' ? 64 : iconSize}
                        color={theme.palette.error.main}
                        style={{
                            filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.1))',
                            zIndex: 1,
                        }}
                    />
                </Box>
                <style>
                    {`
                        @keyframes gentleFloat {
                            0%, 100% { transform: translateY(0px) scale(1); }
                            50% { transform: translateY(-4px) scale(1.02); }
                        }
                        @keyframes rippleEffect {
                            0% { transform: scale(0.8); opacity: 0.6; }
                            100% { transform: scale(1.2); opacity: 0; }
                        }
                    `}
                </style>
            </Box>
        );
    };

    const renderPermissions = () => {
        if (variant !== 'detailed' || requiredPermissions.length === 0) return null;

        return (
            <Card
                elevation={0}
                sx={{
                    mt: 3,
                    mb: 3,
                    p: 3,
                    backgroundColor: alpha(theme.palette.info.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.08)}`,
                    borderRadius: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <RiSecurePaymentLine size={20} color={theme.palette.info.main} />
                    <Typography
                        variant="subtitle1"
                        sx={{
                            ml: 1,
                            color: theme.palette.info.main,
                            fontWeight: 600,
                            fontSize: '0.95rem',
                        }}
                    >
                        Permisos Requeridos
                    </Typography>
                </Box>
                
                <Stack 
                    direction="row" 
                    spacing={1.5} 
                    flexWrap="wrap" 
                    justifyContent="center"
                    sx={{ gap: 1 }}
                >
                    {requiredPermissions.map((perm, index) => (
                        <Chip
                            key={index}
                            icon={<RiLockLine size={14} />}
                            label={
                                perm.permiso 
                                    ? `${perm.modulo.toUpperCase()}.${perm.permiso}` 
                                    : perm.modulo.toUpperCase()
                            }
                            variant="outlined"
                            size="medium"
                            sx={{
                                borderColor: alpha(theme.palette.error.main, 0.2),
                                color: theme.palette.error.main,
                                backgroundColor: alpha(theme.palette.error.main, 0.04),
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                fontFamily: 'monospace',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                                    borderColor: alpha(theme.palette.error.main, 0.3),
                                },
                                transition: 'all 0.2s ease',
                            }}
                        />
                    ))}
                </Stack>
                
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        textAlign: 'center',
                        mt: 2,
                        color: theme.palette.grey[500],
                        fontStyle: 'italic',
                    }}
                >
                    Contacta al administrador para solicitar estos permisos
                </Typography>
            </Card>
        );
    };

    const renderActions = () => {
        if (customActions) return customActions;

        return (
            <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mt: 4 }}
            >
                {showBackButton && (
                    <Button
                        variant="outlined"
                        startIcon={<RiArrowLeftLine size={18} />}
                        onClick={handleGoBack}
                        sx={{
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            borderColor: alpha(theme.palette.grey[400], 0.6),
                            color: theme.palette.grey[700],
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[50], 0.8),
                                borderColor: theme.palette.grey[400],
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        Volver Atrás
                    </Button>
                )}

                {showHomeButton && (
                    <Button
                        variant="contained"
                        startIcon={<RiHome5Line size={18} />}
                        onClick={handleGoHome}
                        sx={{
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.24)}`,
                            border: 'none',
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.32)}`,
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        Ir al Inicio
                    </Button>
                )}

                {showContactInfo && (
                    <Button
                        variant="text"
                        startIcon={<RiContactsBookLine size={18} />}
                        onClick={handleContactAdmin}
                        sx={{
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: theme.palette.info.main,
                            backgroundColor: alpha(theme.palette.info.main, 0.04),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.info.main, 0.08),
                                borderColor: alpha(theme.palette.info.main, 0.3),
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.12)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        Contactar Administrador
                    </Button>
                )}
            </Stack>
        );
    };

    if (variant === 'minimal') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                    maxHeight: '100vh',
                    textAlign: 'center',
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.02)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        p: 4,
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.error.main, 0.08)}`,
                        maxWidth: 480,
                        width: '100%',
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <RiShieldCrossLine 
                            size={56} 
                            color={theme.palette.error.main}
                            style={{ 
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                            }}
                        />
                    </Box>
                    
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.error.main,
                            mb: 2,
                            fontFamily: '"Inter", "SF Pro Display", sans-serif',
                        }}
                    >
                        {title}
                    </Typography>
                    
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.grey[600],
                            mb: 4,
                            lineHeight: 1.6,
                        }}
                    >
                        {message}
                    </Typography>
                    
                    {renderActions()}
                </Card>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                maxHeight: '100vh',
                overflow: 'hidden',
                background: `
                    radial-gradient(ellipse at top left, ${alpha(theme.palette.error.main, 0.04)} 0%, transparent 50%),
                    radial-gradient(ellipse at bottom right, ${alpha(theme.palette.warning.main, 0.03)} 0%, transparent 50%),
                    linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)} 0%, #ffffff 100%)
                `,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: 2, md: 3 },
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 25% 25%, ${alpha(theme.palette.error.main, 0.02)} 0%, transparent 50%),
                        radial-gradient(circle at 75% 75%, ${alpha(theme.palette.primary.main, 0.02)} 0%, transparent 50%)
                    `,
                    pointerEvents: 'none',
                }
            }}
        >
            <Container maxWidth="lg" sx={{ zIndex: 1 }}>
                <Paper
                    elevation={0}
                    sx={{
                        textAlign: 'center',
                        padding: { xs: 4, sm: 5, md: 7 },
                        borderRadius: 5,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(24px)',
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.06),
                        boxShadow: `
                            0 32px 64px -12px ${alpha(theme.palette.error.main, 0.08)},
                            0 0 0 1px ${alpha(theme.palette.error.main, 0.04)}
                        `,
                        position: 'relative',
                        overflow: 'hidden',
                        maxHeight: '90vh',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: `linear-gradient(135deg, 
                                ${theme.palette.error.main} 0%, 
                                ${theme.palette.warning.main} 35%,
                                ${theme.palette.error.main} 70%,
                                ${theme.palette.error.dark} 100%
                            )`,
                        }
                    }}
                >
                    {renderIcon()}

                    {/* Título principal */}
                    <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            color: theme.palette.grey[800],
                            mb: 2,
                            fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
                            fontSize: { xs: '1.75rem', md: '2.75rem' },
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Mensaje descriptivo */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.grey[600],
                            mb: variant === 'detailed' ? 3 : 4,
                            maxWidth: 680,
                            mx: 'auto',
                            lineHeight: 1.8,
                            fontSize: '1.125rem',
                        }}
                    >
                        {message}
                    </Typography>

                    {/* Información adicional para variante detallada */}
                    {variant === 'detailed' && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.grey[500],
                                mb: 1,
                                fontStyle: 'italic',
                                fontSize: '1rem',
                            }}
                        >
                            Si crees que esto es un error, contacta al administrador del sistema.
                        </Typography>
                    )}

                    {renderPermissions()}
                    {renderActions()}

                    {/* Información de contacto adicional */}
                    {showContactInfo && variant === 'detailed' && (
                        <Box
                            sx={{
                                mt: 5,
                                pt: 4,
                                borderTop: '1px solid',
                                borderColor: alpha(theme.palette.grey[200], 0.8),
                            }}
                        >
                            <Stack spacing={1} alignItems="center">
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.grey[600],
                                        fontWeight: 500,
                                    }}
                                >
                                    Portal de Acceso Digital - Poder Judicial
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.grey[400],
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    ID de Sesión: ACCESS_DENIED_{Date.now().toString(36).toUpperCase()}
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default AccessDenied;
